import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Sidebar.module.sass";
import { BiCreditCardAlt, BiUserCircle, BiCabinet, BiShareAlt, BiVideo, BiBook, BiHomeAlt } from "react-icons/bi";
import { Link, NavLink, useHistory } from "react-router-dom";
import cn from "classnames";
import Icon from "../Icon";
import Theme from "../Theme";
import Dropdown from "./Dropdown";
import Image from "../Image";
import { useSelector, useDispatch } from "react-redux";
import { logOut, fetchUpdateUser } from "../../app/user/user.slice";
import { normalizeUserLang } from "../../i18n";

import OutsideClickHandler from "react-outside-click-handler";

const LANG_FLAGS = [
  { code: "en", flag: "/images/flags/en.svg", label: "English" },
  { code: "es", flag: "/images/flags/es.svg", label: "Español" },
  { code: "pt", flag: "/images/flags/pt.svg", label: "Português" },
  { code: "ru", flag: "/images/flags/ru.svg", label: "Русский" },
  { code: "fr", flag: "/images/flags/fr.svg", label: "Français" },
];


const Sidebar = ({ className, onClose, collapsed, setCollapsed }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, bugs, feedback } = useSelector((state) => state);
  const dispatch = useDispatch();

  const bugsCount = bugs?.value?.filter((c) => c?.active)?.length ?? 0;
  const feedbackCount = feedback?.value?.length ?? 0;

  const navigation = [
    {
      title: t("sidebar.home"),
      icon: BiHomeAlt,
      add: false,
      role: ["TEACHER", "ADMIN"],
      url: "/",
    },
    {
      title: t("sidebar.cards"),
      icon: BiCreditCardAlt,
      add: true,
      addTo: "/card/add",
      role: ["TEACHER", "ADMIN"],
      addRole: ["ADMIN"],
      urlDropdown: "/cards",
      slug: "cards",
      counterInHead: true,
      counterBug: true,
      dropdown: [
        {
          title: t("sidebar.bugsReports"),
          url: "/bug-reports",
          counterBug: true,
          colorCounter: "#B5E4CA",
        },
      ],
    },
    {
      title: t("sidebar.catalog"),
      icon: BiCabinet,
      add: false,
      role: ["ADMIN"],
      addRole: ["ADMIN"],
      url: "/catalog",
    },
    {
      title: t("sidebar.topics"),
      icon: BiBook,
      role: ["ADMIN"],
      addRole: ["ADMIN"],
      url: "/topics",
      addTo: "/topic/add",
      add: true,
    },
    {
      title: t("sidebar.users"),
      icon: BiUserCircle,
      add: false,
      role: ["ADMIN"],
      addRole: ["ADMIN"],
      urlDropdown: "/users",
      slug: "users",
      counterInHead: true,
      counterFeedback: true,
      dropdown: [
        // Campañas = el nuevo sistema de notificaciones (news fue retirado).
        { title: t("sidebar.notifications"), url: "/campaigns" },
        // Hilos de soporte (feedback/bugs como chat con el usuario).
        // El feedback ya no es una pantalla aparte: es una conversación más.
        {
          title: "Conversaciones",
          url: "/conversations",
          counterFeedback: true,
          colorCounter: "#FFBC99",
        },
        { title: t("sidebar.classes"), url: "/classes" },
      ],
    },
    {
      title: t("sidebar.socialHub"),
      icon: BiShareAlt,
      role: ["ADMIN"],
      addRole: ["ADMIN"],
      url: "/social-hub",
    },
    {
      title: t("sidebar.videotutorials"),
      icon: BiVideo,
      role: ["ADMIN"],
      addRole: ["ADMIN"],
      url: "/videotutorials",
    },
  ];

  return (
    <>
      <div className={cn(styles.sidebar, className, { [styles.collapsed]: collapsed })}>
        <button className={styles.close} onClick={onClose}>
          <Icon name="close" size="24" />
        </button>
        <div style={{position: 'relative'}}>
          <Link className={styles.logo} to="/" onClick={onClose}>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 28, color: '#AAAAAA' }}>
              <Image className={styles.pic} src="/images/logo-dark.png" srcDark="/images/logo-light.png" alt="Core" />
              {!collapsed && (
                <>
                  <span style={{marginLeft: 15, textTransform: 'capitalize'}}>
                    {user?.value?.role}
                  </span>
                  {process.env.REACT_APP_API_URL.includes('dev') &&
                    <div style={{borderRadius: '6px', marginLeft: 15, fontSize: 15, paddingInline: '8px', background: '#EC55551A', color: '#EC5555'}}>
                      DEV
                    </div>
                  }
                </>
              )}
            </div>
          </Link>
        </div>
        <div className={styles.menu}>
          {navigation?.filter((c) => c?.role?.includes(user?.value?.role)).map((x, index) =>
            x.url ? (
              <React.Fragment key={index}>
                {x?.onClick ? (
                  <div className={styles.item} style={{ cursor: "pointer" }} onClick={x.onClick}>
                    <x.icon size="24" />
                    {x.title}
                  </div>
                ) : (
                  <NavLink
                    className={styles.item}
                    activeClassName={styles.active}
                    to={x.url}
                    key={index}
                    exact
                    onClick={onClose}
                  >
                    <x.icon size="24" />
                    {x.title}
                    {x?.add && x?.addRole?.includes(user?.value?.role) && (
                      <span
                        role="button"
                        tabIndex={0}
                        className={cn(styles.add)}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          history.push(x.addTo);
                          onClose?.();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            history.push(x.addTo);
                            onClose?.();
                          }
                        }}
                      >
                        <Icon name="plus" size="10" style={{ transform: "translateX(0.4em)" }} />
                      </span>
                    )}
                  </NavLink>
                )}
              </React.Fragment>
            ) : (
              <Dropdown
                className={styles.dropdown}
                visibleSidebar={!collapsed}
                setValue={(val) => { if (val) setCollapsed(false); }}
                key={index}
                collapsed={collapsed}
                item={{
                  ...x,
                  dropdown: x.dropdown?.map((d) => ({
                    ...d,
                    counter: d.counterBug ? bugsCount : d.counterFeedback ? feedbackCount : d.counter,
                  })),
                }}
                countInHead={
                  x.counterInHead
                    ? x.counterBug
                      ? bugsCount
                      : x.counterFeedback
                        ? feedbackCount
                        : 0
                    : undefined
                }
                colorCounterInHead={x.counterBug ? "#B5E4CA" : x.counterFeedback ? "#FFBC99" : undefined}
                onClose={onClose}
              />
            )
          )}
        </div>
        <button className={styles.toggle} onClick={() => setCollapsed(!collapsed)}>
          <Icon name={collapsed ? "arrow-right" : "arrow-left"} size="24" />
        </button>
        <div className={styles.foot}>
          <OutsideClickHandler onOutsideClick={() => setUserMenuOpen(false)}>
            <div className={cn(styles.userRow)}>
              <button className={styles.userAvatar} onClick={() => setUserMenuOpen(!userMenuOpen)}>
                {user?.value?.avatarUrl ? (
                  <img src={user?.value?.avatarUrl} alt="Avatar" />
                ) : (
                  <span>
                    {user?.value?.name?.split('')?.[0]?.toUpperCase()}
                    {user?.value?.name?.split('')?.[1]?.toUpperCase()}
                  </span>
                )}
              </button>
              <div className={styles.userEmail}>{user?.value?.email}</div>
              <Theme className={styles.theme} />
              {userMenuOpen && (
                <div className={styles.userDropdown}>
                  <div className={styles.langRow}>
                    <span className={styles.langLabel}>{t("sidebar.language")}</span>
                    <div className={styles.langFlags}>
                      {LANG_FLAGS.map(({ code, flag }) => {
                        const currentLang = normalizeUserLang(user?.value?.lang) || "en";
                        const isSelected = currentLang === code;
                        return (
                          <button
                            key={code}
                            type="button"
                            className={cn(styles.langFlag, { [styles.langFlagSelected]: isSelected })}
                            onClick={() => {
                              if (currentLang !== code) {
                                dispatch(fetchUpdateUser({ lang: code }));
                              }
                              setUserMenuOpen(false);
                            }}
                            title={LANG_FLAGS.find((l) => l.code === code)?.label}
                            aria-label={LANG_FLAGS.find((l) => l.code === code)?.label}
                          >
                            <img src={flag} alt="" width={24} height={18} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.userDropdownItem}
                    onClick={() => {
                      dispatch(logOut());
                      setUserMenuOpen(false);
                    }}
                  >
                    {t("sidebar.logOut")}
                  </button>
                </div>
              )}
            </div>
          </OutsideClickHandler>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
